import React, { useEffect, useMemo, useRef, useState } from "react";
import { News } from "./Information";
import ExitButton from "./ExitButton";
import logo from "../img/beaver_icon.webp";
import backButtonPc from "../img/button-back_pc.svg";
import backButtonSp from "../img/button-back_sp.svg";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import parse from "html-react-parser";
import { siteOrigin } from "../utils/siteOrigin";

gsap.registerPlugin(ScrollTrigger);

const SITE_NAME = "Beaver Delivery";
const DEFAULT_OG_IMAGE_PATH = "/usage_image.webp";

/** news.json の "YYYY-MM-DD HH:mm:ss" を Schema.org / meta 用の ISO8601（JST）にする */
function toIso8601Jst(dateStr: string | undefined): string | undefined {
  if (!dateStr?.trim()) return undefined;
  const normalized = dateStr.trim().replace(" ", "T");
  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(normalized)) {
    return normalized;
  }
  return `${normalized}+09:00`;
}

function stripHtmlForMeta(html: string): string {
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n");
  const noTags = withBreaks.replace(/<[^>]+>/g, "");
  return noTags.replace(/\s+/g, " ").trim();
}

function truncateMeta(text: string, max = 155): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

const InformationDetail = ({ news }: { news?: News[] }) => {
  const params = useParams();
  const location = useLocation();
  const [article, setArticle] = useState<News>();
  const base_url = process.env.REACT_APP_DIR ?? "";
  useEffect(() => {
    setArticle(news?.find((content) => params.slug === content.lp_slug));
  }, [news, params.slug]);

  const origin = siteOrigin();
  const canonicalUrl = `${origin}${base_url}/news/${params.slug ?? ""}`;
  const metaDescription = useMemo(() => {
    if (!article) return "";
    const fromBody = article.body
      ? truncateMeta(stripHtmlForMeta(article.body))
      : "";
    if (fromBody) return fromBody;
    return truncateMeta(article.title);
  }, [article]);

  const articlePlainBody = useMemo(() => {
    if (!article) return "";
    if (!article.body) return article.title;
    return stripHtmlForMeta(article.body);
  }, [article]);

  const ogImageUrl = `${origin}${DEFAULT_OG_IMAGE_PATH}`;

  const jsonLd = useMemo(() => {
    if (!article) return null;
    const published = toIso8601Jst(article.created_at);
    const modified = toIso8601Jst(article.updated_at) ?? published;
    const publisherId = `${origin}/#organization`;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": publisherId,
          name: SITE_NAME,
          url: `${origin}${base_url}/`,
          logo: {
            "@type": "ImageObject",
            url: `${origin}/favicon.png`,
          },
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${canonicalUrl}#breadcrumb`,
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "ホーム",
              item: `${origin}${base_url}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "お知らせ一覧",
              item: `${origin}${base_url}/news`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
              item: canonicalUrl,
            },
          ],
        },
        {
          "@type": "Article",
          "@id": `${canonicalUrl}#article`,
          headline: article.title,
          description: metaDescription,
          articleBody: articlePlainBody,
          datePublished: published,
          dateModified: modified,
          inLanguage: "ja-JP",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": canonicalUrl,
          },
          image: [ogImageUrl],
          author: { "@id": publisherId },
          publisher: { "@id": publisherId },
        },
      ],
    };
  }, [
    article,
    articlePlainBody,
    base_url,
    canonicalUrl,
    metaDescription,
    ogImageUrl,
    origin,
  ]);

  const imgRef = useRef(null);
  useEffect(() => {
    const el = imgRef.current;
    gsap.fromTo(
      el,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        scrollTrigger: {
          trigger: el,
        },
      }
    );
  }, []);

  const publishedIso = article ? toIso8601Jst(article.created_at) : undefined;
  const modifiedIso = article ? toIso8601Jst(article.updated_at) : undefined;
  const pageTitle = article
    ? `${article.title} | ${SITE_NAME}`
    : `お知らせ | ${SITE_NAME}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        {article && metaDescription ? (
          <meta name="description" content={metaDescription} />
        ) : null}
        {article ? <link rel="canonical" href={canonicalUrl} /> : null}
        {article ? (
          <>
            <meta property="og:type" content="article" />
            <meta property="og:locale" content="ja_JP" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={article.title} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={ogImageUrl} />
            <meta
              property="og:image:alt"
              content={`${SITE_NAME} お知らせ`}
            />
            {publishedIso ? (
              <meta
                property="article:published_time"
                content={publishedIso}
              />
            ) : null}
            {modifiedIso ? (
              <meta property="article:modified_time" content={modifiedIso} />
            ) : null}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={article.title} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={ogImageUrl} />
          </>
        ) : null}
        {jsonLd ? (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        ) : null}
      </Helmet>
      <div className="bg-gray-200 w-[100vw] h-[100vh] fixed top-0 left-0 z-[3]">
        <div
          className="bg-[#F2EFE9] h-[85vh] max-w-[1020px] w-[94vw] md:w-[75vw] mx-auto mt-[7vh] rounded-lg"
          ref={imgRef}
        >
          <div className="h-[81vh] max-w-[960px] w-[88vw] md:w-[71vw] mx-auto translate-y-[2vh] rounded-lg ">
            <div className="flex w-[95%] mx-auto pt-[2vh] items-start">
              <Link to={`${base_url}/`}>
                <ExitButton />
              </Link>
              <Link
                className="z-20"
                to={`${base_url}/news`}
                state={{ backgroundLocation: location }}
              >
                <img
                  className="hidden md:block h-[75px] ml-3"
                  src={backButtonPc}
                  alt="バックボタン"
                />
                <img
                  className="block md:hidden h-[32px] ml-3"
                  src={backButtonSp}
                  alt="バックボタン"
                />
              </Link>
            </div>
            <article className="flex flex-col" aria-labelledby="news-article-title">
              <div className="flex mx-auto md:ml-auto md:mr-[2%] leading-4 md:leading-none text-[#ffffff] md:text-[#000000] bg-[#F6C544] md:px-0 h-[2em] w-[11em] text-[16px] md:text-[24px] justify-center items-center rounded-[8px] mt-4 md:mt-0 translate-y-0 md:-translate-y-[6vh] order-2 md:order-1">
                {article?.created_at && publishedIso ? (
                  <time dateTime={publishedIso}>{article.created_at}</time>
                ) : (
                  article?.created_at ?? null
                )}
              </div>
              <header className="block mx-auto mt-0 md:-mt-[3vh] order-1 md:order-2">
                <img
                  className="mx-auto z-10 relative w-[25%] md:w-[15%]"
                  src={logo}
                  alt="ロゴ"
                />
                <h1
                  className="bg-white rounded-[8px] text-[16px] md:text-[24px] w-[90%] md:w-[80%] mx-auto font-bold -mt-[4%] md:-mt-[2.5%] z-0 px-10 py-5"
                  id="news-article-title"
                >
                  {article?.title || ""}
                </h1>
              </header>
              <div className="bg-white rounded-[8px] w-[90%] md:w-[96%] h-[38vh] md:h-[36vh] mx-auto mt-2 md:mt-[2vh] py-6 md:py-10 px-4 md:px-8 text-[16px] md:text-[24px] text-left font-bold overflow-scroll order-3">
                <div className="whitespace-pre-wrap">
                  {parse(article?.body || "")}
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </>
  );
};

export default InformationDetail;
